/**
 * Editor de template — /templates/:id.
 *
 * Para e-mail, três jeitos de chegar ao HTML, em abas:
 *   **Código** — cola/edita o HTML direto (quem já tem template pronto);
 *   **Visual** — a prévia renderizada, com as variáveis trocadas por exemplos;
 *   **Gerar com IA** — briefing → assunto + pré-header + HTML no padrão EGBA.
 *
 * A paleta de variáveis insere `{{nome}}` no ponto do cursor e é a MESMA lista
 * que o motor preenche — variável fora do catálogo aparece como aviso amarelo,
 * porque no envio ela vira vazio.
 */

import { VARIAVEIS_TEMPLATE, variaveisDesconhecidas } from '@egba-do/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { api, type Item } from '../../api/cliente'
import { MiniEmail } from '../../componentes/templates/MiniEmail'
import { AreaTexto, Aviso, Botao, Campo, Carregando, Cartao, Vazio } from '../../componentes/ui'
import { useAuth } from '../../estado/auth'
import { NOME_CANAL, type Template } from './tipos'

type Aba = 'codigo' | 'visual' | 'ia'

export function EditorTemplate() {
  const { id } = useParams<{ id: string }>()
  const navegar = useNavigate()
  const qc = useQueryClient()
  const { usuario } = useAuth()

  const [t, setT] = useState<Template | null>(null)
  const [sujo, setSujo] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const [aba, setAba] = useState<Aba>('visual')
  const [briefing, setBriefing] = useState('')
  const [paraTeste, setParaTeste] = useState('')
  const adotado = useRef<string | null>(null)
  const caixaCodigo = useRef<HTMLTextAreaElement>(null)
  const caixaTexto = useRef<HTMLTextAreaElement>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['template', id],
    queryFn: () => api.get<Item<Template>>(`/api/v1/templates/${id}`),
    retry: 1,
  })

  const { data: ia } = useQuery({
    queryKey: ['estudio-estado'],
    queryFn: () => api.get<Item<{ disponivel: boolean }>>('/api/v1/estudio/estado'),
  })

  useEffect(() => {
    if (!data || adotado.current === data.dados.atualizadoEm) return
    adotado.current = data.dados.atualizadoEm
    setT(data.dados)
    setSujo(false)
  }, [data])

  useEffect(() => {
    if (usuario?.email) setParaTeste((atual) => atual || usuario.email)
  }, [usuario])

  const salvar = useMutation({
    mutationFn: () =>
      api.put<Item<Template> & { avisos?: { variaveisDesconhecidas: string[] } }>(
        `/api/v1/templates/${id}`,
        {
          nome: t!.nome,
          canal: t!.canal,
          assunto: t!.assunto ?? undefined,
          preHeader: t!.preHeader ?? undefined,
          fromNome: t!.fromNome ?? undefined,
          fromEmail: t!.fromEmail ?? undefined,
          corpoHtml: t!.corpoHtml ?? undefined,
          corpoTexto: t!.corpoTexto,
        },
      ),
    onSuccess: (r) => {
      adotado.current = r.dados.atualizadoEm
      setSujo(false)
      setErro(null)
      setAviso('Template salvo.')
      void qc.invalidateQueries({ queryKey: ['templates'] })
    },
    onError: (e: Error) => setErro(e.message),
  })

  // Manda o RASCUNHO atual — salvo ou não — para a caixa de quem edita. O
  // servidor troca as variáveis pelos exemplos e prefixa [TESTE] no assunto.
  const teste = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; provedor: string }>('/api/v1/templates/teste', {
        para: paraTeste.trim(),
        assunto: t?.assunto || t?.nome || 'Teste de template',
        preHeader: t?.preHeader || undefined,
        fromNome: t?.fromNome || undefined,
        fromEmail: t?.fromEmail || undefined,
        corpoHtml: t?.corpoHtml ?? '',
        corpoTexto: t?.corpoTexto ?? '',
      }),
    onSuccess: () => {
      setErro(null)
      setAviso(`Teste enviado para ${paraTeste.trim()} — confira a caixa de entrada (e o spam).`)
    },
    onError: (e: Error) => setErro(e.message),
  })

  const gerar = useMutation({
    mutationFn: () =>
      api.post<Item<{ assunto: string; preHeader: string; corpoHtml: string; corpoTexto: string }>>(
        '/api/v1/estudio/template',
        { briefing },
      ),
    onSuccess: (r) => {
      mudar({
        assunto: r.dados.assunto,
        preHeader: r.dados.preHeader,
        corpoHtml: r.dados.corpoHtml,
        corpoTexto: r.dados.corpoTexto,
      })
      setAba('visual')
      setAviso('E-mail gerado — revise e salve.')
    },
    onError: (e: Error) => setErro(e.message),
  })

  function mudar(mudanca: Partial<Template>): void {
    setT((atual) => (atual ? { ...atual, ...mudanca } : atual))
    setSujo(true)
    setAviso(null)
  }

  /** Insere {{variavel}} no cursor da caixa ativa (código ou texto). */
  function inserirVariavel(nome: string): void {
    const caixa = t?.canal === 'email' && aba === 'codigo' ? caixaCodigo.current : caixaTexto.current
    const marcador = `{{${nome}}}`
    if (!caixa) {
      mudar(
        t?.canal === 'email' && aba === 'codigo'
          ? { corpoHtml: `${t?.corpoHtml ?? ''}${marcador}` }
          : { corpoTexto: `${t?.corpoTexto ?? ''}${marcador}` },
      )
      return
    }
    const inicio = caixa.selectionStart ?? caixa.value.length
    const fim = caixa.selectionEnd ?? caixa.value.length
    const novo = caixa.value.slice(0, inicio) + marcador + caixa.value.slice(fim)
    if (t?.canal === 'email' && aba === 'codigo') mudar({ corpoHtml: novo })
    else mudar({ corpoTexto: novo })
    requestAnimationFrame(() => {
      caixa.focus()
      caixa.selectionStart = caixa.selectionEnd = inicio + marcador.length
    })
  }

  useEffect(() => {
    function aoTeclar(e: KeyboardEvent): void {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault()
        if (sujo && !salvar.isPending) salvar.mutate()
      }
    }
    window.addEventListener('keydown', aoTeclar)
    return () => window.removeEventListener('keydown', aoTeclar)
  })

  useEffect(() => {
    if (!sujo) return
    const avisar = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', avisar)
    return () => window.removeEventListener('beforeunload', avisar)
  }, [sujo])

  if (isLoading) return <Carregando />

  if (!t) {
    return (
      <Vazio
        titulo="Template não encontrado"
        descricao={(error as Error | null)?.message ?? 'Ele pode ter sido removido.'}
        acao={<Botao onClick={() => void navegar('/templates')}>Ver os templates</Botao>}
      />
    )
  }

  const desconhecidas = variaveisDesconhecidas(
    `${t.assunto ?? ''} ${t.preHeader ?? ''} ${t.corpoHtml ?? ''} ${t.corpoTexto}`,
  )
  const ehEmail = t.canal === 'email'

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <button
            type="button"
            onClick={() => {
              if (sujo && !window.confirm('Há alterações não salvas. Sair mesmo assim?')) return
              void navegar('/templates')
            }}
            className="mb-1 text-sm text-slate-500 hover:text-slate-900"
          >
            ← Templates
          </button>
          <h2 className="text-xl font-semibold text-slate-900">{t.nome}</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            {NOME_CANAL[t.canal]}
            {sujo && <span className="ml-2 text-amber-600">• não salvo</span>}
          </p>
        </div>
        <Botao disabled={!sujo} carregando={salvar.isPending} onClick={() => salvar.mutate()}>
          {sujo ? 'Salvar' : 'Salvo'}
        </Botao>
      </div>

      {erro && <Aviso tom="perigo">{erro}</Aviso>}
      {aviso && !erro && <Aviso tom="sucesso">{aviso}</Aviso>}
      {desconhecidas.length > 0 && (
        <Aviso tom="atencao" titulo="Variável fora do catálogo — vira vazio no envio">
          {desconhecidas.map((v) => `{{${v}}}`).join(' · ')}
        </Aviso>
      )}

      <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
        {/* --------------------------------------------------------- campos */}
        <div className="space-y-4">
          <Cartao titulo="Identificação">
            <div className="space-y-4">
              <Campo rotulo="Nome" value={t.nome} onChange={(e) => mudar({ nome: e.target.value })} />
              {ehEmail && (
                <>
                  <Campo
                    rotulo="Assunto"
                    value={t.assunto ?? ''}
                    onChange={(e) => mudar({ assunto: e.target.value })}
                    ajuda="Aceita variáveis: Publicação de {{razaoSocial}}"
                  />
                  <Campo
                    rotulo="Pré-header"
                    value={t.preHeader ?? ''}
                    onChange={(e) => mudar({ preHeader: e.target.value })}
                    ajuda="A linha cinza depois do assunto na caixa de entrada."
                  />
                  <Campo
                    rotulo="Nome do remetente"
                    value={t.fromNome ?? ''}
                    onChange={(e) => mudar({ fromNome: e.target.value })}
                    placeholder="EGBA — Diário Oficial"
                  />
                  <Campo
                    rotulo="E-mail do remetente"
                    type="email"
                    value={t.fromEmail ?? ''}
                    onChange={(e) => mudar({ fromEmail: e.target.value })}
                    placeholder="publicacoes@egba.ba.gov.br"
                    ajuda="Vazio usa o remetente padrão do sistema."
                  />
                </>
              )}
            </div>
          </Cartao>

          {ehEmail && (
            <Cartao
              titulo="E-mail de teste"
              descricao="Manda o conteúdo atual — mesmo sem salvar — com as variáveis de exemplo e o assunto prefixado [TESTE]. Não cria lead nem conta em métrica."
            >
              <div className="space-y-3">
                <Campo
                  rotulo="Enviar para"
                  type="email"
                  value={paraTeste}
                  onChange={(e) => setParaTeste(e.target.value)}
                />
                <Botao
                  variante="secundario"
                  carregando={teste.isPending}
                  disabled={!paraTeste.includes('@') || !(t.corpoHtml ?? '').trim()}
                  onClick={() => teste.mutate()}
                >
                  {teste.isPending ? 'Enviando…' : 'Enviar teste'}
                </Botao>
              </div>
            </Cartao>
          )}

          <Cartao
            titulo="Variáveis"
            descricao="Clique para inserir. São preenchidas por destinatário no envio."
          >
            <div className="space-y-3">
              {(['pessoa', 'empresa', 'sistema'] as const).map((grupo) => (
                <div key={grupo}>
                  <p className="rotulo capitalize">{grupo}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {VARIAVEIS_TEMPLATE.filter((v) => v.grupo === grupo).map((v) =>
                      v.disponivel === 'sim' ? (
                        <button
                          key={v.nome}
                          type="button"
                          title={`${v.rotulo} — ex.: ${v.exemplo}`}
                          onClick={() => inserirVariavel(v.nome)}
                          className="rounded-md border border-[color:var(--color-borda)] px-2 py-1 font-mono text-[11px] text-marca-700 transition hover:border-marca-400 hover:bg-marca-50"
                        >
                          {`{{${v.nome}}}`}
                        </button>
                      ) : (
                        <span
                          key={v.nome}
                          title={`${v.rotulo} — disponível quando as bases internas da EGBA (sócios e contatos) forem importadas.`}
                          className="cursor-not-allowed rounded-md border border-dashed border-[color:var(--color-borda)] px-2 py-1 font-mono text-[11px] text-slate-300"
                        >
                          {`{{${v.nome}}}`}
                        </span>
                      ),
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Cartao>
        </div>

        {/* ------------------------------------------------------- conteúdo */}
        <div className="space-y-3">
          {ehEmail ? (
            <>
              <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1 text-sm">
                {(
                  [
                    ['visual', 'Visual'],
                    ['codigo', 'Código (HTML)'],
                    ['ia', 'Gerar com IA'],
                  ] as Array<[Aba, string]>
                ).map(([chave, rotulo]) => (
                  <button
                    key={chave}
                    type="button"
                    onClick={() => setAba(chave)}
                    className={`rounded-md px-3 py-1.5 transition ${
                      aba === chave
                        ? 'bg-white font-medium text-marca-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-900'
                    }`}
                  >
                    {rotulo}
                  </button>
                ))}
                <span className="ml-auto pr-2 text-xs text-slate-400">
                  {aba === 'visual' ? 'variáveis com valores de exemplo' : ''}
                </span>
              </div>

              {aba === 'visual' && (
                <MiniEmail html={t.corpoHtml ?? ''} altura="calc(100vh - 320px)" interativa />
              )}

              {aba === 'codigo' && (
                <>
                  <textarea
                    ref={caixaCodigo}
                    value={t.corpoHtml ?? ''}
                    onChange={(e) => mudar({ corpoHtml: e.target.value })}
                    spellCheck={false}
                    className="h-[calc(100vh-380px)] min-h-72 w-full rounded-lg border border-[color:var(--color-borda)] bg-slate-950 p-4 font-mono text-[13px] leading-relaxed text-slate-100 focus:ring-2 focus:ring-marca-500 focus:outline-none"
                  />
                  <AreaTexto
                    rotulo="Versão texto (leitores sem HTML)"
                    rows={3}
                    value={t.corpoTexto}
                    onChange={(e) => mudar({ corpoTexto: e.target.value })}
                  />
                </>
              )}

              {aba === 'ia' && (
                <Cartao
                  titulo="Gerar com IA"
                  descricao="Descreva o e-mail; a IA monta assunto, pré-header e o HTML no padrão EGBA — só com as variáveis do catálogo e os fatos legais do sistema. O resultado substitui o conteúdo atual."
                >
                  {ia?.dados.disponivel ? (
                    <div className="space-y-4">
                      <AreaTexto
                        rotulo="O que este e-mail precisa dizer"
                        rows={5}
                        value={briefing}
                        onChange={(e) => setBriefing(e.target.value)}
                        ajuda="Ex.: primeiro aviso da obrigação de publicar concluintes, tom cordial, citar a Res. 44/2020, botão para a landing."
                      />
                      <Botao
                        carregando={gerar.isPending}
                        disabled={briefing.trim().length < 20}
                        onClick={() => gerar.mutate()}
                      >
                        {gerar.isPending ? 'Gerando…' : 'Gerar e-mail'}
                      </Botao>
                    </div>
                  ) : (
                    <Aviso tom="atencao">
                      Nenhum provedor de IA configurado no ambiente — as abas Código e Visual
                      continuam funcionando normalmente.
                    </Aviso>
                  )}
                </Cartao>
              )}
            </>
          ) : (
            <>
              <AreaTexto
                rotulo="Mensagem"
                rows={8}
                value={t.corpoTexto}
                onChange={(e) => mudar({ corpoTexto: e.target.value })}
                ajuda={
                  t.canal === 'sms'
                    ? `${t.corpoTexto.length} caracteres — SMS cobra por bloco de 160.`
                    : `${t.corpoTexto.length} caracteres.`
                }
              />
              <div>
                <p className="rotulo">Como chega</p>
                <div className="max-w-sm rounded-xl rounded-tl-sm bg-emerald-50 p-3 text-[13px] leading-relaxed whitespace-pre-wrap text-slate-800">
                  {t.corpoTexto
                    .replace(/\{\{\s*primeiroNome\s*\}\}/g, 'Maria')
                    .replace(/\{\{\s*razaoSocial\s*\}\}/g, 'Escola Exemplo Ltda')
                    .replace(/\{\{\s*linkLanding\s*\}\}/g, 'https://…/lp/concluintes') || '—'}
                </div>
              </div>
            </>
          )}
          <p className="text-[11px] text-slate-400">
            <kbd>Ctrl</kbd>+<kbd>S</kbd> salva. A régua escolhe este template pelo nome, na ação.
          </p>
        </div>
      </div>
    </div>
  )
}
